import React, { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Button } from 'primereact/button'

import { Dialog } from '../../../../../common/uikit/Dialog'
import { Input } from '../../../../../common/uikit/Input'
import { useToast } from '../../../../../common/uikit/ToastProvider'
import { MAX_NAME_LENGTH } from '../../../../../common/constants/InputsLength'
import { isFetchBaseQueryError } from '../../../../../common/redux/api/fetchBaseQueryWithReauth'
import { type ChargePointSetOwnerApiArg, useChargePointSetOwnerMutation } from '../../../../../common/redux/api/web-api'
import useText from '../../../../../common/hooks/useText'

const formId = 'add-charge-point-form'

type FormValues = Pick<ChargePointSetOwnerApiArg, 'chargePointId' | 'name' | 'wifiPassword'>

export type AddChargePointDialogProps = {
    visible: boolean
    onHide: () => void
    userId: string
}

export function AddChargePointDialog({ visible, onHide, userId }: AddChargePointDialogProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors, isSubmitted, isSubmitting, isValid },
    } = useForm<FormValues>({
        defaultValues: {
            chargePointId: '',
            name: '',
            wifiPassword: '',
        },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })
    const nameLength = useWatch({ control, name: 'name', defaultValue: '' }).trim().length
    const [addChargerToUser, { isLoading }] = useChargePointSetOwnerMutation()

    useEffect(() => {
        if (visible) reset()
    }, [reset, visible])

    const doAddChargerToUser = async (formValues: FormValues) => {
        const name = formValues.name.trim()

        try {
            await addChargerToUser({
                userId,
                chargePointId: formValues.chargePointId,
                name: name || formValues.chargePointId,
                wifiPassword: formValues.wifiPassword,
            }).unwrap()

            toast.notifySuccess(text('msgChargerWasSuccessfullyAdded'))
            onHide()
        } catch (error: unknown) {
            if (
                isFetchBaseQueryError(error) &&
                (error.status === 409 || error.status === 404 || error.status === 403) &&
                error.data &&
                (error.data as { error: string }).error
            ) {
                toast.notifyError((error.data as { error: string }).error)
            } else {
                toast.notifyError(text('msgChargerNotAddedtoUser'))
            }
        }
    }

    const submitDisabled = isLoading || isSubmitting || (isSubmitted && !isValid)
    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={onHide} />
            <Button
                label={text('btnAddChargerToUser')}
                type="submit"
                form={formId}
                disabled={submitDisabled}
                loading={isLoading || isSubmitting}
            />
        </div>
    )

    return (
        <Dialog
            data-testid="add-charge-point-dialog"
            header={text('lblAddChargerToUserDialogTitle')}
            visible={visible}
            onHide={onHide}
            footer={footer}
        >
            <form
                id={formId}
                data-testid="create-modal-form"
                onSubmit={handleSubmit(doAddChargerToUser)}
                className="flex flex-column gap-3"
                noValidate
            >
                <Input
                    id="serialNumber"
                    label={text('lblSerialNumberField')}
                    {...register('chargePointId', {
                        required: text('msgAddChargePointSerialNumberRequired'),
                    })}
                    error={errors.chargePointId?.message}
                    data-testid="serialNumber"
                    required
                />
                <Input
                    id="pinCode"
                    label={text('lblPin')}
                    {...register('wifiPassword', { required: text('msgPinCodeRequired') })}
                    error={errors.wifiPassword?.message}
                    data-testid="pinCode"
                    required
                />
                <Input
                    id="name"
                    label={text('lblName')}
                    {...register('name', {
                        validate: (value) => value.trim().length <= MAX_NAME_LENGTH || text('lblNameError'),
                    })}
                    error={errors.name?.message}
                    subtitle={
                        <span className="flex justify-content-end">
                            {text('msgTextAreaCounter', {
                                textCount: nameLength,
                                maxLength: MAX_NAME_LENGTH,
                            })}
                        </span>
                    }
                    data-testid="name"
                />
            </form>
        </Dialog>
    )
}
